import { useState } from "react";
import { Save } from "lucide-react";
import { useWorldStore } from "../../stores/world.js";
import { useCanvasStore, type BuilderEdgeType } from "../../stores/canvas.js";
import { SaveTopologyModal } from "../topology/SaveTopologyModal.js";

const EDGE_TYPE_OPTIONS: Array<{ value: BuilderEdgeType; label: string }> = [
  { value: "delegates-to", label: "delegates" },
  { value: "reports-to", label: "reports" },
  { value: "peers-with", label: "peers" },
  { value: "pipes-to", label: "pipes" },
];

const toggleBtn =
  "px-3 py-1.5 text-xs font-medium rounded-md transition-[color,background-color,box-shadow,transform] duration-200 active:scale-[0.96]";

export function CanvasToolbar() {
  const { mode, setMode, viewMode, setViewMode } = useWorldStore();
  const defaultEdgeType = useCanvasStore((s) => s.defaultEdgeType);
  const setDefaultEdgeType = useCanvasStore((s) => s.setDefaultEdgeType);
  const nodeCount = useCanvasStore((s) => s.nodes.length);
  const [saveOpen, setSaveOpen] = useState(false);

  const isBuilding = viewMode === "flow" && mode === "design";

  return (
    <>
      <div className="absolute top-4 left-4 z-canvas-ui flex items-center gap-2">
        {/* View mode toggle */}
        <div className="flex items-center gap-0.5 rounded-lg border border-border-subtle/20 bg-surface-light/60 p-0.5 backdrop-blur-sm">
          <button
            onClick={() => setViewMode("flow")}
            className={`${toggleBtn} ${
              viewMode === "flow"
                ? "bg-brand-cyan text-white shadow-glow-cyan"
                : "text-text-secondary hover:text-text-primary"
            }`}
          >
            Graph
          </button>
          <button
            onClick={() => setViewMode("pixel")}
            className={`${toggleBtn} ${
              viewMode === "pixel"
                ? "bg-brand-cyan text-white shadow-glow-cyan"
                : "text-text-secondary hover:text-text-primary"
            }`}
          >
            Pixel
          </button>
        </div>

        {/* Monitor/Design toggle (only for graph view) */}
        {viewMode === "flow" && (
          <div className="flex items-center gap-0.5 rounded-lg border border-border-subtle/20 bg-surface-light/60 p-0.5 backdrop-blur-sm">
            <button
              onClick={() => setMode("monitor")}
              className={`${toggleBtn} ${
                mode === "monitor"
                  ? "bg-brand-purple text-white shadow-glow-purple"
                  : "text-text-secondary hover:text-text-primary"
              }`}
            >
              Monitor
            </button>
            <button
              onClick={() => setMode("design")}
              className={`${toggleBtn} ${
                mode === "design"
                  ? "bg-brand-pink text-white shadow-glow-pink"
                  : "text-text-secondary hover:text-text-primary"
              }`}
            >
              Design
            </button>
          </div>
        )}

        {/* Builder controls — only while designing */}
        {isBuilding && (
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 rounded-lg border border-border-subtle/20 bg-surface-light/60 px-2 py-1 backdrop-blur-sm">
              <span className="text-[10px] uppercase tracking-wide text-text-muted">Edge</span>
              <select
                value={defaultEdgeType}
                onChange={(e) => setDefaultEdgeType(e.target.value as BuilderEdgeType)}
                className="bg-transparent text-xs text-text-primary focus:outline-none"
                title="Type for new connections"
              >
                {EDGE_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value} className="bg-surface-light">
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <button
              onClick={() => setSaveOpen(true)}
              disabled={nodeCount === 0}
              className="flex min-h-[32px] items-center gap-1.5 rounded-lg bg-brand-purple px-3 py-1.5 text-xs font-medium text-white transition-[background-color,box-shadow,transform] duration-200 hover:shadow-glow-purple active:scale-[0.96] disabled:opacity-40"
            >
              <Save className="h-3.5 w-3.5" />
              Save
            </button>
          </div>
        )}
      </div>

      <SaveTopologyModal open={saveOpen} onClose={() => setSaveOpen(false)} />
    </>
  );
}
